// Ref. https://github.com/lhilario/zebra-browser-print-wrapper
import axios from 'axios';
import { getErrorMessage } from '@/utils/error';

const API_URL = 'http://localhost:9100/';
const fetcher = axios.create({
  headers: {
    'Accept-Language': 'ko,en-US;q=0.9,en;q=0.8',
    'Content-Type': 'text/plain;charset=UTF-8'
  }
});

interface Device {
  name: string;
  deviceType: string;
  connection: string;
  uid: string;
  provider: string;
  manufacturer: string;
  version: number;
}

function handlePrinterError(error: unknown) {
  if (error instanceof Error) {
    if (error.message.indexOf('Network Error') >= 0) {
      return '프로그램 설치 상태 또는 프로그램이 현재 실행 중인지 확인해 주세요. 확인 완료 후 재시도 부탁드립니다.';
    }
    if (error.message.indexOf('status code 500') >= 0) {
      return '프린터 전원 상태를 확인해 주세요. 프린터 전원이 켜진 상태일 경우 송장 출력 프로그램이 현재 실행 중인지 확인해 주세요. 확인 완료 후 재시도 부탁드립니다.';
    }
  }
  return getErrorMessage(error);
}

export default class ZebraBrowserPrinter {
  device: Device = {} as Device;

  setPrinter = (device: Device) => {
    this.device = device;
  };

  getPrinter = (): Device => {
    return this.device;
  };

  getDefaultPrinter = async (): Promise<Device | undefined> => {
    const endpoint = API_URL + 'default';

    try {
      const res = await fetcher.get(endpoint);
      const { data } = res;

      if (data?.split?.('\n\t').length === 7) {
        const deviceRaw = data.split('\n\t');

        const name = this.cleanUpString(deviceRaw[1]);
        const deviceType = this.cleanUpString(deviceRaw[2]);
        const connection = this.cleanUpString(deviceRaw[3]);
        const uid = this.cleanUpString(deviceRaw[4]);
        const provider = this.cleanUpString(deviceRaw[5]);
        const manufacturer = this.cleanUpString(deviceRaw[6]);

        return {
          connection,
          deviceType,
          manufacturer,
          name,
          provider,
          uid,
          version: 0
        };
      } else {
        throw new Error(
          '송장 출력 프로그램에서 프린터 설정 상태를 확인해주세요. 확인 완료 후 재시도 부탁드립니다.'
        );
      }
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  };

  getAvailablePrinters = async () => {
    const endpoint = API_URL + 'available';

    try {
      const res = await fetcher.get(endpoint);
      const { data } = res;

      if (!data?.printer) {
        throw new Error(
          '프린터 전원 상태를 확인하셨나요? 전원이 켜진 상태일 경우 송장 출력 프로그램 상태를 확인해 주세요. 확인 완료 후 재시도 부탁드립니다.'
        );
      } else {
        return data.printer;
      }
    } catch (error: unknown) {
      throw new Error(handlePrinterError(error));
    }
  };

  cleanUpString = (str: string): string => {
    const arr = str.split(':');
    const result = arr[1].trim();
    return result;
  };

  checkPrinterStatus = async () => {
    await this.write('~HQES');
    const result = await this.read();
    const isError = result?.charAt(70);
    let isReadyToPrint = false;

    isReadyToPrint = isError === '0';

    if (!isReadyToPrint) {
      throw new Error(
        '프린터 용지와 잉크 상태를 확인해주세요. 계속해서 문제가 발생할 경우 닥터나우 파트너센터로 연락 부탁드립니다.'
      );
    } else {
      return result;
    }
  };

  write = async (data: string) => {
    try {
      const endpoint = API_URL + 'write';
      const res = await fetcher.post(endpoint, {
        device: this.device,
        data
      });

      return res.data;
    } catch (error) {
      throw new Error(handlePrinterError(error));
    }
  };

  read = async () => {
    try {
      const endpoint = API_URL + 'read';
      const res = await fetcher.post(endpoint, {
        device: this.device
      });

      return res.data;
    } catch (error) {
      throw new Error(handlePrinterError(error));
    }
  };

  print = async (text: string) => {
    try {
      return this.write(text);
    } catch (error) {
      throw new Error(handlePrinterError(error));
    }
  };
}
